import { faClockRotateLeft } from "@fortawesome/free-solid-svg-icons"
import {
  DropdownInput,
  FlexBox,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { useTimerModalStore } from "./TimerModal"
import { ExpandCard } from "../../../../components/card/ExpandCard"
import { getDoubleDigitNum } from "../../../../components/timerInput/TimerInput"
import { timerMinuteOptions, timerSecondOptions } from "../../../../constants"
import useMobile from "../../../../hooks/useMobile"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

export const TimerSection = () => {
  const {
    timerLength,
    minutes,
    seconds,
    setMinutes,
    setSeconds,
    setTimerLength,
    selectedQuest,
  } = useAddEditQuestStore()
  const { onOpen: onOpenTimerModal, setShowTimer } = useTimerModalStore()
  const { t } = useTranslation()

  const isMobile = useMobile()

  const localizedMinuteOptions = timerMinuteOptions.map((o) => ({
    ...o,
    label: `${o.value} ${t("QUEST_MIN_ABBR")}`,
  }))
  const localizedSecondOptions = timerSecondOptions.map((o) => ({
    ...o,
    label: `${o.value} ${t("QUEST_SEC_ABBR")}`,
  }))

  useEffect(() => {
    if (seconds?.value === "0") {
      setSeconds(null)
    }
    if (minutes?.value === "0") {
      setMinutes(null)
    }
    if (minutes?.value > 0 || seconds?.value > 0) {
      let timeString = `00:${getDoubleDigitNum(minutes?.value)}:${getDoubleDigitNum(
        seconds?.value
      )}`
      setTimerLength(timeString)
    }
  }, [minutes, seconds, setTimerLength, setMinutes, setSeconds])

  useEffect(() => {
    // for editing quest
    if (!!selectedQuest?.timer_length) {
      setTimerLength(selectedQuest.timer_length)
      const minuteValue = parseInt(selectedQuest.timer_length.split(":")[1], 10)
      const secondValue = parseInt(selectedQuest.timer_length.split(":")[2], 10)
      setMinutes(
        minuteValue === 0
          ? null
          : {
              value: minuteValue.toString(),
              label: `${minuteValue} ${t("QUEST_MIN_ABBR")}`,
            }
      )
      setSeconds(
        secondValue === 0
          ? null
          : {
              value: secondValue.toString(),
              label: `${secondValue} ${t("QUEST_SEC_ABBR")}`,
            }
      )
    }
    // for quest templates
    if (!timerLength) return
    const minutes = parseInt(timerLength.split(":")[1], 10)
    const seconds = parseInt(timerLength.split(":")[2], 10)
    setMinutes(
      minutes === 0
        ? null
        : {
            value: minutes.toString(),
            label: `${minutes} ${t("QUEST_MIN_ABBR")}`,
          }
    )
    setSeconds(
      seconds === 0
        ? null
        : {
            value: seconds.toString(),
            label: `${seconds} ${t("QUEST_SEC_ABBR")}`,
          }
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ExpandCard
      icon={faClockRotateLeft}
      text={
        <Typography
          variant="bodySmallBold"
          color={
            minutes?.value > 0 || seconds?.value > 0
              ? JoonUIColor.text.primary
              : JoonUIColor.text.secondary
          }
        >
          {minutes?.value > 0 || seconds?.value > 0
            ? t("QUEST_TIMER_FOR", {
                minutes: minutes?.value ?? "0",
                minuteUnit: t(
                  minutes?.value === "1"
                    ? "QUEST_MINUTE_SINGULAR"
                    : "QUEST_MINUTE_PLURAL"
                ),
                seconds: seconds?.value ?? "0",
                secondUnit: t(
                  seconds?.value === "1"
                    ? "QUEST_SECOND_SINGULAR"
                    : "QUEST_SECOND_PLURAL"
                ),
              })
            : t("QUEST_ADD_TIMER")}
        </Typography>
      }
      additionalContent={
        <FlexBox
          direction="row"
          justify="center"
          align="center"
          gap={SPACING.space2}
          wrap={false}
        >
          <button
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%" }}
          >
            <DropdownInput
              name="minute"
              options={localizedMinuteOptions}
              label={minutes?.label}
              onSelect={setMinutes}
              placeholder={t("QUEST_MINUTES_PH")}
              style={{ background: JoonUIColor.background.primaryNeutral }}
              menuStyle={{ minWidth: "200px" }}
              fullWidth
            />
          </button>

          <button
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%" }}
          >
            <DropdownInput
              name="second"
              options={localizedSecondOptions}
              label={seconds?.label}
              onSelect={setSeconds}
              placeholder={t("QUEST_SECONDS_PH")}
              style={{ background: JoonUIColor.background.primaryNeutral }}
              menuStyle={{ minWidth: "200px" }}
              fullWidth
            />
          </button>
        </FlexBox>
      }
      reset={minutes !== null || seconds !== null}
      onReset={() => {
        setMinutes(null)
        setSeconds(null)
        setShowTimer(false)
      }}
      onClick={() => {
        if (isMobile) return onOpenTimerModal()
      }}
    />
  )
}
